import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import { Fragment, useMemo } from 'react'
import { IconButton, SkeletonText } from 'utoniq-ui'

import { formatNumber } from '@/clients/utoniq-core'
import {
  CollectionFeeDetailFragment,
  Event,
  FeeDetailFragment,
  ItemType,
  Maybe,
  PaymentDetailFragment,
  StorePurchaseHistoryFragment,
  StorePurchaseOrderDetailQuery,
  UserAddressFragment,
  UserPaymentType,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import {
  OrderItem,
  OrderItemDetailSkeleton,
  PaymentInfo,
  PurchaseInfo,
  ShippingAddress,
} from '@/components/organisms'
import {
  TotalPaymentCell,
  TotalPaymentCellProps,
} from '@/components/pages/StoreItemPurchaseProceed/template'
import { config } from '@/config'
import { useCurrentUser, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { DateTime } from '@/core/entities'
import { encodeSkuId } from '@/core/sku'
import { useAnalytics } from '@/lib/analytics'
import { openIntercom, updateIntercom } from '@/lib/intercom'
export * from './fragments'

export type State = {
  loading: boolean
  userCartTransactionOrder: Maybe<StorePurchaseHistoryFragment>
  prepaidPointBalance: Maybe<
    StorePurchaseOrderDetailQuery['prepaidPointBalance']
  >
  paymentSource?: Maybe<PaymentDetailFragment>
  fullName: string
  address: Maybe<UserAddressFragment['address']>
  phoneNumber: string
  isPhoneNumberVerified: boolean
  systemFee: Maybe<CollectionFeeDetailFragment>[]
  shippingFee: Maybe<FeeDetailFragment>
  konbiniFee: Maybe<FeeDetailFragment>
  refundedAmount: number | undefined
  canceledAt: string | undefined
  cartItemsByCollection: {
    collection: {
      id: string
      title: string
    } | null
    items: StorePurchaseHistoryFragment['items']
  }[]
  events: (Maybe<Pick<Event, 'id' | 'title' | 'note'>> | undefined)[]
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const StorePurchaseOrderDetailTemplate: Template<State, Action> = ({
  state,
}) => {
  const { t } = useTranslation('storePurchaseOrderDetail')
  const { routerNames, routerPages } = useRouter()

  const itemType = useMemo<ItemType | null>(() => {
    if (!state.userCartTransactionOrder) {
      return null
    }
    return state.userCartTransactionOrder.items?.[0]?.item?.type
  }, [state.userCartTransactionOrder])

  // 個人情報の登録必須項目
  const personalInformationRequirements = useMemo<string[]>(() => {
    if (!state.userCartTransactionOrder) {
      return []
    }

    const result = state.userCartTransactionOrder.items.reduce(
      (acc: string[], item) => {
        const requirements = item.item.personalInformationRequirements

        return Array.isArray(requirements) ? acc.concat(requirements) : acc
      },
      []
    )

    return [...new Set(result)]
  }, [state.userCartTransactionOrder])

  const isReceiveTime = useMemo(() => {
    return state.cartItemsByCollection?.some(group => {
      return state.userCartTransactionOrder?.pickupUsers?.find(
        ({ collectionId }) => collectionId === group.collection?.id
      )?.formattedPickupStartEndTime
    })
  }, [state.cartItemsByCollection, state.userCartTransactionOrder])

  return (
    <BaseLayout
      margin
      header={{
        left: (
          <IconButton.Back
            edge="start"
            size="medium"
            href={routerPages.StorePurchaseOrders}
          />
        ),
        title: t('title'),
      }}
    >
      <Head title={routerNames.StorePurchaseOrderDetail} />
      <OrderDetailCell
        userCartTransactionOrder={state.userCartTransactionOrder}
        canceledAt={state.canceledAt}
        cartItemsByCollection={state.cartItemsByCollection}
      />
      <PurchaseInfo
        loading={state.loading}
        artist={state.userCartTransactionOrder?.items?.[0]?.item?.issuedArtist}
      />
      <PaymentInfo
        loading={state.loading && !state.userCartTransactionOrder?.paymentType}
        formattedPrepaidPointBalance={
          state.prepaidPointBalance?.formattedPrepaidPointBarance ?? 0
        }
        purchaseType={
          state.userCartTransactionOrder?.isKonbini
            ? 'konbini'
            : (state.userCartTransactionOrder?.paymentType as UserPaymentType)
        }
        paymentSource={state.paymentSource}
      />

      {!!personalInformationRequirements.length && (
        <ShippingAddress
          loading={state.loading}
          fullName={state.fullName}
          address={state.address}
          isPhoneNumberVerified={state.isPhoneNumberVerified}
          phoneNumber={state.phoneNumber}
          personalInformationRequirements={personalInformationRequirements}
        />
      )}
      {state.loading && !state.userCartTransactionOrder?.paymentType ? (
        <PurchaseDetailSkeleton />
      ) : (
        <PurchaseDetailCell
          loading={state.loading}
          purchaseType={
            state.userCartTransactionOrder?.isKonbini
              ? 'konbini'
              : (state.userCartTransactionOrder?.paymentType as UserPaymentType)
          }
          systemFee={state.systemFee}
          shippingFee={state.shippingFee}
          konbiniFee={state.konbiniFee}
          paymentType={
            state.userCartTransactionOrder?.paymentType as UserPaymentType
          }
          totalPrice={state.userCartTransactionOrder?.originalTotalAmount ?? 0}
          refundedAmount={state.refundedAmount}
          formattedTotalPrice={
            state.userCartTransactionOrder?.formattedOriginalTotalAmount ?? '0'
          }
          link
        />
      )}
      <NoteCell
        events={state.events}
        loading={state.loading}
        isReceiveTime={isReceiveTime}
      />
    </BaseLayout>
  )
}

type OrderDetailCellProps = {
  userCartTransactionOrder: State['userCartTransactionOrder']
  canceledAt: State['canceledAt']
  cartItemsByCollection: State['cartItemsByCollection']
}
const OrderDetailCell: Cell<OrderDetailCellProps> = ({
  userCartTransactionOrder,
  canceledAt,
  cartItemsByCollection,
}) => {
  const { t } = useTranslation('storePurchaseOrderDetail')
  const { generatePath, generateQueryPath, routerPages } = useRouter()
  const { analytics } = useAnalytics()

  if (!userCartTransactionOrder) {
    return <OrderItemDetailSkeleton />
  }

  return (
    <CellFrame sx={{ pb: 5 }}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h5" color="text.secondary" sx={{ py: 3 }}>
          {t('orderDetail', '注文内容')}
        </Typography>
        <Button
          variant="text"
          sx={{
            textAlign: 'center',
            fontSize: '14px',
            lineHeight: '20px',
            fontWeight: 600,
          }}
          href={routerPages.MyOwnedItems}
        >
          {t('ownedItems')}
        </Button>
      </Stack>
      <Card elevation={0} sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 5, '&:last-child': { pb: 4 } }}>
          {!!canceledAt && <CanceledCell canceledAt={canceledAt} />}
          <Stack spacing={1} sx={{ pb: 3 }}>
            <Typography variant="body2">
              {t('orderDate', {
                date: userCartTransactionOrder.formattedCreatedAt,
              })}
            </Typography>
          </Stack>
          <Stack spacing={3}>
            <Stack spacing={5}>
              {cartItemsByCollection?.map(group => {
                // コレクションに配送アイテムが含まれているか
                const hasMerch = group.items.some(
                  item => item.item.type === ItemType.Merch
                )
                // コレクションに紐づいている受け取り情報
                const pickupUser = userCartTransactionOrder.pickupUsers?.find(
                  ({ collectionId }) => collectionId === group.collection?.id
                )

                return (
                  <Box className="collectionGroup">
                    {group.collection && (
                      <Box sx={{ mb: 4 }}>
                        <Typography variant="h6">
                          {group.collection?.title}
                        </Typography>
                        {!!pickupUser && (
                          <Typography sx={{ mt: 2 }} variant="subtitle1">
                            {t('pickupTime')}
                            {pickupUser?.formattedPickupStartEndTime}
                          </Typography>
                        )}
                      </Box>
                    )}
                    <Box>
                      {group.items.map(itm => {
                        const skuAmount = itm.skus.reduce((acc, sku) => {
                          if (!sku) return acc
                          return acc + sku.count * (sku.sku?.price ?? 0)
                        }, 0)
                        //get total sku has price greater than 0
                        const skuCount = itm.skus.reduce((acc, sku) => {
                          if (!sku?.sku?.price) return acc
                          return acc + (sku?.count ?? 0)
                        }, 0)
                        const item = {
                          ...itm.item,
                          // 表示金額を、現在のアイテムの金額ではなく、決済時の金額で表示する
                          formattedPrice: formatNumber(
                            (itm.amount - skuAmount) /
                              ((itm.count ?? 1) - skuCount || 1)
                          ),
                        }

                        if (!itm.skus.length) {
                          return (
                            <Link
                              href={generatePath(routerPages.OwnedItemDetail, {
                                itemId: itm.itemId,
                              })}
                            >
                              <OrderItem
                                key={itm.itemId}
                                item={item}
                                quantity={itm.count}
                                arrow
                              />
                            </Link>
                          )
                        }
                        return (
                          <Fragment key={itm.itemId}>
                            {itm.skus?.map(
                              itemSku =>
                                itemSku && (
                                  <Link
                                    href={generateQueryPath(
                                      routerPages.OwnedItemDetail,
                                      { itemId: itm.itemId },
                                      { skuId: encodeSkuId(itemSku.skuId) }
                                    )}
                                  >
                                    <OrderItem
                                      key={itemSku.skuId}
                                      item={item}
                                      quantity={itemSku.count}
                                      sku={itemSku.sku}
                                      arrow
                                    />
                                  </Link>
                                )
                            )}
                          </Fragment>
                        )
                      })}
                    </Box>
                    <Stack sx={{ mt: 3 }}>
                      {/* 配送アイテムが含まれている場合、導線を表示 */}
                      {hasMerch && (
                        <Button
                          size="large"
                          variant="outlined"
                          href={routerPages.DeliveryManagement}
                        >
                          {t('confirmDelivery', '配送状況を確認する')}
                        </Button>
                      )}
                      {/* 受け取りが含まれている場合、導線を表示 */}
                      {!!pickupUser && (
                        <Button
                          size="large"
                          variant="outlined"
                          href={generatePath(routerPages.MyOwnedTicketDetail, {
                            eventId: pickupUser.eventCollection.eventId,
                            collectionId: pickupUser.collectionId,
                            pickupUserId: pickupUser.id,
                          })}
                          onClick={() => {
                            analytics?.track('click_eventPickupDetail', {
                              applicationKey: config.applicationKey,
                              type: 'cartTransactionOrder',
                            })
                          }}
                        >
                          {t('confirmPickup', '受け取り情報を確認する')}
                        </Button>
                      )}
                    </Stack>
                  </Box>
                )
              })}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </CellFrame>
  )
}

type PurchaseDetailCellProps = TotalPaymentCellProps & {
  loading: boolean
}
export const PurchaseDetailCell: Cell<PurchaseDetailCellProps> = ({
  loading,
  ...props
}) => {
  const { t } = useTranslation('storePurchaseOrderDetail')
  if (loading) {
    return <PurchaseDetailSkeleton />
  }
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Typography variant="h5" color="text.secondary" sx={{ py: 3 }}>
        {t('purchaseDetail')}
      </Typography>
      <Card elevation={0} sx={{ borderRadius: 2 }}>
        <CardContent sx={{ px: 5, py: 3, '&:last-child': { pb: 3 } }}>
          <TotalPaymentCell {...props} />
        </CardContent>
      </Card>
    </CellFrame>
  )
}

export const PurchaseDetailSkeleton: Cell = () => (
  <CellFrame sx={{ pb: 5 }}>
    <Box sx={{ py: 2 }}>
      <SkeletonText variant="h3" width="50%" />
    </Box>
    <Card elevation={0} sx={{ borderRadius: 2 }}>
      <CardContent sx={{ px: 5, py: 4, '&:last-child': { pb: 4 } }}>
        {Array.from(Array(3).keys()).map(key => (
          <Stack direction="row" spacing={1} key={key}>
            <Stack width="60%">
              <SkeletonText variant="small" />
            </Stack>
            <Stack width="40%">
              <SkeletonText variant="small" />
            </Stack>
          </Stack>
        ))}
      </CardContent>
    </Card>
  </CellFrame>
)

const CanceledCell = ({ canceledAt }: { canceledAt: string }) => {
  const { t } = useTranslation('storePurchaseOrderDetail')
  const { user } = useCurrentUser()

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Chip
          color="error"
          label={
            <Typography variant="overline" color="white">
              {t('refundAvailable', '返金対応')}
            </Typography>
          }
          size="small"
          sx={{ height: 18 }}
        />
        <Typography variant="subtitle1">
          {t('refundAvailableFor', {
            date: DateTime.formatDate(canceledAt),
          })}
        </Typography>
      </Stack>
      <Stack
        sx={{
          mt: 2,
          mb: 4,
          p: 2,
          borderColor: 'error.main',
          borderWidth: 1,
          borderStyle: 'solid',
        }}
      >
        <Typography variant="caption">
          {t('refundNote')}
          {t('refundNoteLinkPrefix')}
          <Typography
            variant="caption"
            color="primary"
            onClick={() => {
              updateIntercom(user)
              openIntercom()
            }}
          >
            {t('refundNoteLink')}
          </Typography>
          {t('refundNoteLinkSuffix')}
        </Typography>
      </Stack>
    </>
  )
}

type NoteCellProps = {
  loading: boolean
  isReceiveTime?: boolean
  events: State['events']
}
const NoteCell: Cell<NoteCellProps> = ({ events, isReceiveTime, loading }) => {
  const { t } = useTranslation('storePurchaseOrderDetail')

  if (loading || !events.length) {
    return null
  }

  return (
    <CellFrame sx={{ pb: 5 }}>
      <Typography variant="h5" color="text.secondary" sx={{ py: 3 }}>
        {t('notes', '注意事項')}
      </Typography>
      <Card elevation={0} sx={{ borderRadius: 2 }}>
        <CardContent sx={{ px: 5, py: 3, '&:last-child': { pb: 3 } }}>
          {events.map(event => {
            return (
              <Stack spacing={1}>
                <Typography variant="subtitle1">
                  {t('notesTitle', { title: event?.title })}
                </Typography>
                <Typography variant="caption">{event?.note}</Typography>
              </Stack>
            )
          })}
        </CardContent>
      </Card>
    </CellFrame>
  )
}
